import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import useForm from '@afosto/utils-react/validation/useForm';
import useRegisterInput from '../Form/useRegisterInput';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';
import ArrayField from '../ArrayField';
import Address from '../Address';
import Autocomplete from '../Autocomplete';
import Checkbox from '../Checkbox';
import Form from '../Form';
import IBANMask from '../IBANMask';
import Icon from '../Icon';
import CurrencyField from '../CurrencyField';
import TextField from '../TextField';
import FileUpload from '../FileUpload';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Box from '@material-ui/core/Box';
import uuid from '@afosto/utils/uuid';
import getFullName from '@afosto/utils/getFullName';
import { contacts as contactsApi } from '@afosto/contacts/api/Contacts';
import { files as filesApi } from '@afosto/content/api/Files';
import validationSchema from './validations';
import translations from './translations';
import ContactSearch from '../ContactSearch';

const LOGO_FIELD_NAME = 'logo';

const OrganisationForm = ({ organisation, onSubmit, onCancel, inline, showSetAsPrimary }) => {
  const intl = useIntl();
  const [accountManager, setAccountManager] = useState(null);
  const [files, setFiles] = useState([]);

  const fetchAccountManager = async id => {
    try {
      const response = await contactsApi.getContact(id);
      const { data } = response;

      setAccountManager({ ...data, label: getFullName(data), value: data?.id });
    } catch (err) {
      setAccountManager(null);
    }
  };

  const {
    name,
    coc_number: cocNumber,
    payment_settings: paymentSettings = {},
    account_manager: accountManagerId = null,
    file_id: fileID,
    tags,
  } = organisation;
  const primaryAddress = organisation?.addresses?.primary;
  const primaryPhoneNumber = organisation?.phone_numbers?.primary;

  const form = useForm({
    validationSchema,
    editInline: inline,
    defaultValues: {
      name,
      cocNumber,
      isPrimary: false,
      currentDueAmount: paymentSettings?.post_payment?.current_due_amount ?? 0,
      spendingLimit: paymentSettings?.post_payment?.spending_limit,
      iban: (paymentSettings?.post_payment?.iban ?? []).reduce(
        (acc, iban) => [...acc, { iban }],
        [],
      ),
      administrationEmail: organisation?.administration?.email,
      accountManager: accountManager ?? null,
      logo: fileID || null,
      receiveInvoiceInEmail: paymentSettings?.invoice?.is_expected_through_email ?? true,
      receiveInvoiceInMail: paymentSettings?.invoice?.is_expected_through_post_mail ?? false,
      receiveInvoiceAtDelivery: paymentSettings?.invoice?.is_expected_at_delivery ?? false,
      paymentTerm: paymentSettings?.post_payment?.due_after ?? 0,
      tags: tags ?? [],
    },
    onSubmit: async data => {
      if (onSubmit) {
        await onSubmit({ ...data, iban: (data?.iban ?? []).map(value => value.iban) });
      }
    },
    onCancel,
  });
  const { setValue, setIsEditing } = form;

  useEffect(() => {
    setIsEditing(false);
  }, [organisation, setIsEditing]);

  useEffect(() => {
    if (accountManagerId && accountManagerId !== '') {
      fetchAccountManager(accountManagerId).catch(() => {
        // Catch only.
      });
    } else {
      setAccountManager(null);
    }
  }, [organisation, accountManagerId]);

  useRegisterInput(LOGO_FIELD_NAME, true, form);

  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const response = await filesApi.getFile(fileID);
        const { data: file } = response;

        setFiles([
          {
            source: file.id,
            options: {
              type: 'local',
              file: {
                name: file.filename,
                size: file.metadata.size,
                type: file.mime,
              },
            },
          },
        ]);
      } catch (err) {
        setFiles([]);
      }
    };

    if (fileID) {
      fetchLogo();
    }
  }, [fileID]);

  const handleRemoveFile = async fileId => {
    setValue(LOGO_FIELD_NAME, null);

    try {
      await filesApi.deleteFile(fileId);
    } catch (err) {
      // Catch only.
    }
  };

  /**
   * Return the icon for the verification of the registration.
   * @param {object} registration
   * @returns {Icon}
   */
  const getVerificationIcon = registration => {
    if (!registration.verification || registration.verification.is_pending) {
      return <Icon icon={['fal', 'clock']} />;
    }

    if (!registration.verification.is_valid) {
      return <Icon icon={['fal', 'times']} />;
    }

    return <Icon icon={['fal', 'check']} />;
  };

  const ibanFields = [
    {
      key: uuid(),
      name: 'iban',
      InputComponent: TextField,
      inputProps: {
        name: 'iban',
        label: intl.formatMessage(translations.fields.ibanNumber.label),
        InputProps: {
          inputComponent: IBANMask,
        },
      },
    },
  ];

  return (
    <LoadingWrapper isLoading={form.isSubmitting}>
      <Form form={form}>
        <TextField
          name="name"
          label={intl.formatMessage(translations.fields.name.label)}
          readOnly={form.editInline}
        />

        <TextField
          name="cocNumber"
          label={intl.formatMessage(translations.fields.cocNumber.label)}
          readOnly={form.editInline}
        />

        {showSetAsPrimary && (
          <Checkbox
            name="isPrimary"
            label={intl.formatMessage(translations.fields.isPrimary)}
            readOnly={form.editInline}
          />
        )}
        <TextField
          name="paymentTerm"
          label={intl.formatMessage(translations.fields.paymentTerm.label)}
          type="number"
          InputProps={{
            min: 1,
          }}
          readOnly={form.editInline}
        />
        <CurrencyField
          name="spendingLimit"
          label={intl.formatMessage(translations.fields.spendingLimit.label)}
          readOnly={form.editInline}
        />
        <CurrencyField
          name="currentDueAmount"
          label={intl.formatMessage(translations.fields.currentDueAmount.label)}
          readOnly={form.editInline}
        />
        <ContactSearch
          name="accountManager"
          label={intl.formatMessage(translations.fields.accountManager.label)}
          readOnly={form.editInline}
        />
        <TextField
          name="administrationEmail"
          label={intl.formatMessage(translations.fields.administrationEmail.label)}
          readOnly={form.editInline}
        />
        <Checkbox
          name="receiveInvoiceInEmail"
          label={intl.formatMessage(translations.fields.receiveInvoiceInEmail.label)}
          readOnly={form.editInline}
        />
        <Checkbox
          name="receiveInvoiceInMail"
          label={intl.formatMessage(translations.fields.receiveInvoiceInMail.label)}
          readOnly={form.editInline}
        />
        <Checkbox
          name="receiveInvoiceAtDelivery"
          label={intl.formatMessage(translations.fields.receiveInvoiceAtDelivery.label)}
          readOnly={form.editInline}
        />

        <Autocomplete
          name="tags"
          label={intl.formatMessage(translations.fields.tags.label)}
          options={[]}
          readOnly={form.editInline}
          multiple
          enableCreate
        />

        <ArrayField
          name="iban"
          label={intl.formatMessage(translations.fields.iban.label)}
          fields={ibanFields}
          readOnly={form.editInline}
        />

        {!inline && (
          <FormControl variant="outlined" margin="normal" disabled fullWidth>
            <InputLabel shrink variant="outlined">
              {intl.formatMessage(translations.fields.logo.label)}
            </InputLabel>
            <Box pt={2}>
              <FileUpload
                onUploadFile={file => setValue(LOGO_FIELD_NAME, file.id)}
                onRemoveFile={handleRemoveFile}
                onRevertFile={handleRemoveFile}
                files={files}
                allowFileSizeValidation
                maxTotalFileSize="8MB"
                uploadPath="/organisations/logos"
              />
            </Box>
          </FormControl>
        )}

        {inline && (
          <>
            <FormControl variant="outlined" margin="normal" disabled fullWidth>
              <InputLabel shrink variant="outlined">
                {intl.formatMessage(translations.fields.primaryAddress.label)}
              </InputLabel>
              <Box p={2} pt={3}>
                {primaryAddress ? (
                  <Address
                    company={primaryAddress.organisation}
                    fullName={getFullName(primaryAddress)}
                    addressLine1={primaryAddress.address_line_1}
                    addressLine2={primaryAddress.address_line_2}
                    postalCode={primaryAddress.postal_code}
                    city={primaryAddress.locality}
                    country={primaryAddress.country_code}
                    administrativeArea={primaryAddress.administrative_area}
                  />
                ) : (
                  '-'
                )}
              </Box>
            </FormControl>
            <FormControl variant="outlined" margin="normal" disabled fullWidth>
              <InputLabel shrink variant="outlined">
                {intl.formatMessage(translations.fields.primaryPhoneNumber.label)}
              </InputLabel>
              <Box p={2} pt={3}>
                {primaryPhoneNumber?.number ?? '-'}
              </Box>
            </FormControl>
            <FormControl variant="outlined" margin="normal" disabled fullWidth>
              <InputLabel shrink variant="outlined">
                {intl.formatMessage(translations.fields.registrations.label)}
              </InputLabel>
              <Box px={2} pt={2}>
                {organisation?.registrations?.length
                  ? organisation?.registrations.map(registration => (
                      <Box pb={2} display="flex" alignItems="center">
                        <Box pr={2}>{getVerificationIcon(registration)}</Box>
                        <div>
                          {registration?.number}
                          <br />
                          {registration?.verification?.name ?? '-'}
                        </div>
                      </Box>
                    ))
                  : '-'}
              </Box>
            </FormControl>
          </>
        )}
      </Form>
    </LoadingWrapper>
  );
};

OrganisationForm.propTypes = {
  organisation: PropTypes.object,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  inline: PropTypes.bool,
  showSetAsPrimary: PropTypes.bool,
};

OrganisationForm.defaultProps = {
  organisation: {},
  onSubmit: undefined,
  onCancel: undefined,
  inline: false,
  showSetAsPrimary: false,
};

export default OrganisationForm;
